/**
 * URL Sanitizer Plugin for GO Commerce
 * 
 * This plugin ensures all route URLs are properly sanitized before navigation.
 * It prevents Object URLs like [object Object] from being used for navigation.
 */
export default defineNuxtPlugin((nuxtApp) => {
  // Add a hook that runs before every route navigation
  nuxtApp.hook('app:created', () => {
    // Override NuxtLink behavior to catch object URLs
    if (nuxtApp.vueApp) {
      const originalPush = nuxtApp.$router.push;
      nuxtApp.$router.push = (to) => {
        // Safety check for object URLs
        if (to !== null && typeof to === 'object') {
          // If to is an object with a path property, preserve all properties
          if (to.path) {
            console.log('Converting route object to path string:', to, '→', to.path);
            // Pass the entire object to preserve query parameters and other properties
            return originalPush(to);
          }
          
          // If it's an object but no path, use hash as fallback
          console.warn('Invalid route object with no path:', to);
          return originalPush({ path: '/' });
        }

        // If to is undefined, use home path
        if (to === undefined) {
          console.warn('Undefined route detected, redirecting to home');
          return originalPush({ path: '/' });
        }

        // Check if to is a string that contains "[object Object]"
        if (typeof to === 'string' && to.includes('[object Object]')) {
          console.warn('Detected [object Object] in URL, redirecting to home:', to);
          return originalPush({ path: '/' });
        }

        // Normal routing behavior
        return originalPush(to);
      };
    }
  });

  // Add a global object URL protection to all NuxtLink components
  nuxtApp.vueApp.directive('safe-link', {
    mounted(el) {
      // Find anchor elements within the component
      const anchors = el.tagName === 'A' ? [el] : el.querySelectorAll('a');
      
      anchors.forEach(anchor => {
        // Get the original click handler
        const originalClick = anchor.onclick;
        
        // Replace with a safe click handler
        anchor.onclick = (e) => {
          const href = anchor.getAttribute('href');
          if (href && (href === '[object Object]' || href.includes('[object Object]'))) {
            console.warn('Prevented navigation to object URL:', href);
            e.preventDefault();
            return false;
          }
          
          // Call original handler if it exists
          if (originalClick) return originalClick.call(anchor, e);
        };
      });
    }
  });
  
  // Add utility to safely convert any value to a valid URL string
  return {
    provide: {
      safeUrl: (url: any): string => {
        if (url === null || url === undefined) return '/';
        if (typeof url === 'string') return url;
        if (typeof url === 'object') {
          // If it's an object with a path property, use that
          if (url.path) return url.path;
          // If it has a toString method that doesn't return [object Object], use that
          const str = String(url);
          if (str !== '[object Object]') return str;
        }
        // Default fallback
        console.warn('Invalid URL value, using fallback:', url);
        return '/';
      }
    }
  };
});
